import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { DialogService } from '../dialogservice.service'
import { AdminService } from '../utils/services/admin.service'
import { MatSnackBar } from '@angular/material/snack-bar'
import { forkJoin } from 'rxjs'
import { CustomersService } from '../utils/services/customers.service'
import { CommonModule } from '@angular/common'
import { NewUserComponent } from '../new-user/new-user.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { CustomerManagementComponent } from '../customer-management/customer-management.component'
import { PartnerManagementComponent } from '../partner-management/partner-management.component'
import { SwitchModule } from '@syncfusion/ej2-angular-buttons'
import { MatButtonModule } from '@angular/material/button'
import { PartnersService } from '../utils/services/partners.service'
import { MatTooltipModule } from '@angular/material/tooltip'
import { UserSettingsService, UserSettings } from '../utils/services/user-settings.service'

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    SwitchModule,
    NewUserComponent,
    CustomerManagementComponent,
    PartnerManagementComponent,
    SpinnerComponent,
    MatTooltipModule
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
})
export class UserManagementComponent implements OnInit {
  users: any = [];

  selectedUser: any = {};
  isForUpdate: boolean = false;
  deleteConfirmationPopup: boolean = false;
  userForDelete: any = {};
  currentSW: any = {};
  isSpinnerActive: boolean = false;
  deleteDialogConfirmationPopup: boolean = false;

  constructor(
    public dialogService: DialogService,
    public adminService: AdminService,
    private snackBar: MatSnackBar,
    private readonly customersService: CustomersService,
    private readonly partnersService: PartnersService,
    private changeDetectorRef: ChangeDetectorRef,
    private readonly userSettingsService: UserSettingsService
  ) {}

  ngOnInit() {
    this.getAllKeyCloakUser();
  }

  /**
   * Handle toggle for daily report preference and persist to backend user-settings.
   * @param event Syncfusion switch change event
   * @param user Selected row user (Keycloak user)
   */
  onDailyReportSwitch(event: any, user: any) {
    const checked = !!event.checked
    user.wantsDailyReport = checked

    const keycloakId = user.id as string
    const settingsId = user.userSettingsId as number | undefined

    if (settingsId) {
      this.userSettingsService.update(settingsId, { wantsDailyReport: checked }).subscribe({
        next: (updated) => {
          user.userSettingsId = updated.id
        },
        error: (err) => {
          console.error('Failed to update user settings', { keycloakId, settingsId, err })
          this.snackBar.open('Einstellung konnte nicht gespeichert werden.', '', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          })
          // rollback UI state
          user.wantsDailyReport = !checked
        }
      })
      return
    }

    this.userSettingsService.create({ keycloakId, wantsDailyReport: checked }).subscribe({
      next: (created) => {
        user.userSettingsId = created.id
      },
      error: (err) => {
        console.error('Failed to create user settings', { keycloakId, err })
        this.snackBar.open('Einstellung konnte nicht gespeichert werden.', '', {
          duration: 4000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
        })
        // rollback UI state
        user.wantsDailyReport = !checked
      }
    })
  }

  onClickAddUser() {
    this.dialogService.showNewUserDialog = true;
    this.isForUpdate = false;
    this.selectedUser = {};
  }

  onClickUser(user: any) {
    this.selectedUser = user;
    this.isForUpdate = true;
    this.dialogService.showNewUserDialog = true;
  }

  userChange(event: any) {
    this.dialogService.showNewUserDialog = false;

    const userObject = { ...event };
    const wasTemporaryPassword = userObject.temporary === true;
    delete userObject.customerName;

    if (this.isForUpdate) {
      const { credentials, ...rest } = userObject;
      this.isSpinnerActive = true;
      this.adminService.updateKeyCloakUser({ ...rest, id: this.selectedUser.id })
      .subscribe({
        next: (res :any) => {
          const updatedUser = res.data; 
          if (!updatedUser) {
            console.warn('No updated user data returned from server');
            return;
          }
          
          // Prüfen, ob sich die Rolle geändert hat
          let newRole = 'Clients'; 
          let newRolle = 'Client';  
          
          if (updatedUser.groups?.includes('Admins')) {
            newRole = 'Admins';
            newRolle = 'Admin';
          } else if (updatedUser.groups?.includes('Partners')) {
            newRole = 'Partners';
            newRolle = 'Partner';
          }
          
          const oldRole = this.selectedUser.role;
          
          // Wenn sich die Rolle geändert hat oder wir einen Customer-Namen haben
          if (oldRole !== newRole || event.customerName) {
            console.log(`Rolle hat sich geändert: ${oldRole} -> ${newRole} oder Customer wurde aktualisiert`);
            // Kurze Verzögerung, um sicherzustellen, dass Backend-Operationen abgeschlossen sind
            setTimeout(() => {
              // Nur die relevanten Daten neu laden
              const loadCustomers = new Promise<void>((resolve) => {
                this.customersService.fetchAll(() => {
                  console.log('Customers neu geladen');
                  resolve();
                });
              });
              
              const loadPartners = new Promise<void>((resolve) => {
                this.partnersService.fetchAll(() => {
                  console.log('Partners neu geladen');
                  resolve();
                });
              });
              
              // Warten, bis beide Datenquellen neu geladen wurden
              Promise.all([loadCustomers, loadPartners]).then(() => {
                // Den aktualisierten Benutzer in der Liste aktualisieren
                const index = this.users.findIndex(
                  (user: any) => user.id === this.selectedUser.id
                );
                
                if (index !== -1) {
                  // Benutzer in der Liste aktualisieren
                  this.users[index] = {
                    ...this.users[index],
                    ...updatedUser,
                    role: newRole,
                    rolle: newRolle,
                  };
                }
                
                this.snackBar.open('Benutzer erfolgreich aktualisiert.', '', {
                  duration: 4000,
                  horizontalPosition: 'end',
                  verticalPosition: 'top',
                });
              });
            }, 500);
          } else {
            // Wenn sich die Rolle nicht geändert hat, nur den Benutzer in der Liste aktualisieren
            const index = this.users.findIndex(
              (user: any) => user.id === this.selectedUser.id
            );
    
            if (index === -1) {
              console.warn('Could not find user in local array to update');
              return;
            }
            
            this.users[index] = {
              ...this.users[index],   
              ...updatedUser,       
              role: newRole,
              rolle: newRolle,
            };
            
            this.snackBar.open('Benutzer erfolgreich aktualisiert.', '', {
              duration: 4000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
            });
          }

          if (event.customerName) {
            this.changeCustomer(this.selectedUser.id, event.customerName);
          }
          // Benachrichtigung wird in den Bedingungszweigen angezeigt
          this.isSpinnerActive = false;
        },
        error: (err) => {
          console.error('Error updating user:', err);
          this.snackBar.open('Benutzer konnte nicht aktualisiert werden.', '', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        }
      });

    } else {
      this.adminService.createKeyCloakUser(userObject).subscribe({
        next: (res) => {
          if (wasTemporaryPassword) {
            this.snackBar.open('Benutzer erfolgreich erstellt. Ein temporäres Passwort wurde per E-Mail an den Benutzer gesendet.', '', {
              duration: 6000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
            });
          }
          
          this.getAllKeyCloakUser(() => {
            if (event.customerName) {
              const id = this.users.find((user: any) => user.username === (event.username as string).toLocaleLowerCase())?.id;
              this.changeCustomer(id, event.customerName);
            }
          });
        },
        error: (error: any) => {
          let message = 'Benutzer kann nicht erstellt werden';
          if (
            error.error.message ==
            'Failed to create user: User exists with same username'
          ) {
            message =
              'Benutzer kann nicht erstellt werden: Benutzer existiert mit demselben Benutzernamen';
          } else if (
            error.error.message ==
            'Failed to create user: User exists with same email'
          ) {
            message =
              'Benutzer kann nicht erstellt werden: Benutzer existiert mit derselben E-Mail';
          }
          this.snackBar.open(message, '', {
            duration: 4000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        },
      });
    }
  }

  private changeCustomer(userId: string, customerName: string) {
    const customer = {
      id: this.customersService.customers.find((customer) => customer.userId === userId)?.id,
      userId: userId,
      customerName: customerName
    };
    this.customersService.isForUpdate = customer.id ? true : false;
    this.customersService.change(customer);
  }

  public getAllKeyCloakUser(fn?: () => void): void {
    this.isSpinnerActive = true;

    const userStreams = [
      this.adminService.getAllKeyCloakUserGroupVise('Admins'),
      this.adminService.getAllKeyCloakUserGroupVise('Clients'),
      this.adminService.getAllKeyCloakUserGroupVise('Partners')
    ];

    forkJoin(userStreams).subscribe({
      next: ([adminsResponse, clientsResponse, partnersResponse]) => {
        this.users = [
          ...adminsResponse.map((user) => ({ ...user, role: 'Admins', rolle: 'Admin' })),
          ...clientsResponse.map((user) => ({ ...user, role: 'Clients', rolle: 'Client' })),
          ...partnersResponse.map((user) => ({ ...user, role: 'Partners', rolle: 'Partner' }))
        ];

        // Load user settings for each user to get wantsDailyReport state
        this.enrichUsersWithSettings()

        if (fn) {
          fn();
        }
      },
      error: (error: any) => {
        this.users = [];

        console.error('Error fetching users:', error);
      }
    });
  }

  /**
   * Enrich `users` with their persisted settings.
   * Adds `wantsDailyReport` (default false) and `userSettingsId` for later updates.
   */
  private enrichUsersWithSettings(): void {
    if (!Array.isArray(this.users) || this.users.length === 0) {
      this.isSpinnerActive = false
      return
    }

    let completed = 0
    const total = this.users.length

    for (const user of this.users) {
      const keycloakId = user.id as string
      this.userSettingsService.getByKeycloakId(keycloakId).subscribe({
        next: (settings: UserSettings | null) => {
          user.wantsDailyReport = settings?.wantsDailyReport ?? false
          user.userSettingsId = settings?.id
        },
        error: (err) => {
          console.warn('Failed to load user settings', { keycloakId, err })
          user.wantsDailyReport = false
          user.userSettingsId = undefined
        },
        complete: () => {
          completed++
          if (completed >= total) {
            this.isSpinnerActive = false
          }
        }
      })
    }
  }

  onUserEnabledSwitch(event: any, user: any, sw: any) {
    this.userForDelete = user;
    this.currentSW = sw;
    this.users.find((u: any) => u.id === user.id).enabled = event.checked;
    if (event.checked) {
      this.adminService
        .activateKeyCloakUser(this.userForDelete.id)
        .subscribe(() => {
          this.deleteConfirmationPopup = false;
          this.userForDelete = {};
          this.currentSW = {};
          this.customersService.fetchAll(() => {});
        });
    } else {
      this.deleteConfirmationPopup = true;
    }
  }

  onClickDelete(user: any) {
    this.userForDelete = user;
    this.deleteDialogConfirmationPopup = !this.deleteConfirmationPopup;
    this.currentSW = {};
  }

  onClickDeleteJa() {
    this.adminService
      .deleteUser(this.userForDelete.id)
      .subscribe(() => {
        this.deleteDialogConfirmationPopup = false;
        this.users = this.users.filter((u: any) => u.id !== this.userForDelete.id);
        this.userForDelete = {};
        this.currentSW = {};
        this.customersService.fetchAll(() => {});
      });
  }

  onClickDeleteNein() {
      this.deleteDialogConfirmationPopup = !this.deleteDialogConfirmationPopup;
      this.userForDelete = {};
      this.currentSW = {};
  }

  onClickJa() {
    this.adminService
      .deleteKeyCloakUser(this.userForDelete.id)
      .subscribe(() => {
        this.deleteConfirmationPopup = false;
        this.userForDelete = {};
        this.currentSW = {};
        this.customersService.fetchAll(() => {});
      });
  }

  onClickNein() {
    setTimeout(() => {
      this.userForDelete.enabled = true;
      this.currentSW.checked = true;
      this.changeDetectorRef.detectChanges();

      this.deleteConfirmationPopup = !this.deleteConfirmationPopup;
      this.userForDelete = {};
      this.currentSW = {};
    }, 500);
  }
}
